import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { PhotoDisplay } from "./PhotoDisplay";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const downloadPhoto = async (url) => {
  try {
    let blob, filename = "photo.jpg";
    if (url.includes("/api/files/")) {
      const fileId = url.split("/api/files/").pop().split("?")[0];
      const token = localStorage.getItem("token");
      const res = await axios.get(`${BACKEND_URL}/api/files/${fileId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const { data, content_type, filename: fn } = res.data;
      const bytes = Uint8Array.from(atob(data), (c) => c.charCodeAt(0));
      blob = new Blob([bytes], { type: content_type });
      filename = fn || `photo-hd.${(content_type || "image/jpeg").split("/")[1] || "jpg"}`;
    } else {
      const res = await fetch(url);
      blob = await res.blob();
      filename = url.split("/").pop().split("?")[0] || "photo.jpg";
    }
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
  } catch (e) {
    toast.error("Erreur lors du téléchargement");
  }
};

export const Lightbox = ({ images, currentIndex, onClose, onNext, onPrev }) => {
  useEffect(() => {
    if (!images || images.length === 0) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft') onPrev();
    };
    document.addEventListener('keydown', handleKeyDown);
    // Prevent page scroll while lightbox is open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [images, onClose, onNext, onPrev]);

  if (!images || images.length === 0) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/95 z-[9999] flex items-center justify-center" onClick={onClose} data-testid="lightbox-overlay">
      <button onClick={onClose} className="absolute top-4 right-4 text-white/70 hover:text-white p-2 z-10" data-testid="lightbox-close">
        <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12" /></svg>
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); downloadPhoto(images[currentIndex]); }}
        className="absolute top-4 right-16 text-white/70 hover:text-white p-2 z-10"
        title="Télécharger en HD"
        data-testid="lightbox-download">
        <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16" /></svg>
      </button>
      {images.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); onPrev(); }} className="absolute left-4 text-white/70 hover:text-white p-2 z-10">
            <svg className="w-10 h-10" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M15 19l-7-7 7-7" /></svg>
          </button>
          <button onClick={(e) => { e.stopPropagation(); onNext(); }} className="absolute right-4 text-white/70 hover:text-white p-2 z-10">
            <svg className="w-10 h-10" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" /></svg>
          </button>
        </>
      )}
      <div onClick={(e) => e.stopPropagation()} className="flex flex-col items-center">
        <PhotoDisplay url={images[currentIndex]} className="max-w-[90vw] max-h-[85vh] object-contain" />
        {images.length > 1 && <p className="text-white/70 mt-4">{currentIndex + 1} / {images.length}</p>}
      </div>
    </div>,
    document.body
  );
};

export const ClickablePhoto = ({ url, alt, className, allPhotos = [], photoIndex = 0 }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(photoIndex);
  const photos = allPhotos.length > 0 ? allPhotos : (url ? [url] : []);
  
  const openLightbox = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setCurrentIndex(photoIndex);
    setLightboxOpen(true);
  };
  const handleNext = () => setCurrentIndex((prev) => (prev + 1) % photos.length);
  const handlePrev = () => setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length);
  
  return (
    <>
      <div onClick={openLightbox} className="cursor-pointer w-full h-full">
        <PhotoDisplay url={url} className={className} alt={alt} />
      </div>
      {lightboxOpen && (
        <Lightbox images={photos} currentIndex={currentIndex} onClose={() => setLightboxOpen(false)} onNext={handleNext} onPrev={handlePrev} />
      )}
    </>
  );
};
